import get from "lodash.get"
import dayjs from "dayjs"
import { parseFactValue } from "@/utils"

/**
 * @description
 * Will go through appliedDiscounts and returns originalPrice
 * of first applied discount which is premium before discounts
 * @param {*} pricing Object
 */
const getPremiumBeforeDiscounts = pricing => {
	if (
		!pricing ||
		!pricing.appliedDiscounts ||
		pricing.appliedDiscounts.length === 0
	)
		return null

	const { appliedDiscounts } = pricing
	// Get all discount codes and order it based on how they were applied
	// Return first appliedDiscount.originalPrice
	const sortedAppliedDiscounts = appliedDiscounts.sort(
		(a, b) => a.order - b.order
	)
	return sortedAppliedDiscounts[0].originalPrice
}

// Return premium before discount/original price of policy
const getOriginalPrice = policy => {
	const originalprice = get(policy, "acceptedOffer.premium.originalPrice", null)
	return originalprice
}

// Return formatted discounts array of a policy
const getDiscounts = policy => {
	let discounts = get(policy, "acceptedOffer.premium.appliedDiscounts")
	if (discounts)
		return discounts.map((item, index) => {
			return `${item.code} (${item.ratio * 100}%) (${item.formattedFlat})`
		})
}

// Check if exclusions exist by checking if a exclusion id exists
// Returns boolean
const hasExclusions = policy => {
	const exclusions = get(policy, "acceptedOffer.exclusions[0].id", null)
	return exclusions ? true : false
}

// Return formatted exclusions array of a policy
const getExclusions = policy => {
	const exclusions = get(policy, "acceptedOffer.exclusions")
	if (exclusions)
		return exclusions.map((item, index) => {
			return `${item.codeName.code} - Remarks:${item.remark}`
		})
}

// Return exclusion code based on index param
const getExclusionCode = (policy, index) => {
	const exclusions = get(policy, "acceptedOffer.exclusions", [])
	if (exclusions) {
		let exclusionsArray = exclusions.map(item => {
			return item.codeName.code
		})
		return exclusionsArray[index]
	} else return null
}

// Return rejection code based on index param
const getRejectionCode = (policy, index) => {
	const rejections = get(policy, "rejectionCodes", [])
	if (rejections) {
		let rejectionsArray = rejections.map(item => {
			return item.code
		})
		return rejectionsArray[index]
	} else return null
}

// Return rejection codes in rejections Array
const getRejectionCodesArray = policy => {
	const rejectionCodes = get(policy, "rejectionCodes")
	if (rejectionCodes) return rejectionCodes.map(item => item.code)
}

// Return rejection remarks of policy
const getRejectionRemarks = policy => {
	const rejectionRemarks = get(policy, "rejectionRemarks")
	if (rejectionRemarks) return rejectionRemarks
}

// Return cancellation reason
const getCancellationReason = policy => {
	const cancellationReason = get(policy, "cancellationReason")
	if (cancellationReason) return cancellationReason
}

// Check if policy has appliedTaxes.code === hkIALevy
// Return Levy Ratio
const getLevyRatio = policy => {
	const taxes = get(policy, "acceptedOffer.premium.appliedTaxes")
	let levy
	if (taxes) levy = taxes.find(item => item.code === "hkIALevy")
	if (levy) return levy.ratio
	else return ""
}

// Check if policy has appliedTaxes.code === hkIALevy
// Return Levy Flat
const getLevyFlat = policy => {
	const taxes = get(policy, "acceptedOffer.premium.appliedTaxes")
	let levy
	if (taxes) levy = taxes.find(item => item.code === "hkIALevy")
	if (levy) return levy.flat
}

// Check if loadings exist by checking if a loading id exists
// Returns boolean
const hasLoadings = policy => {
	const loadings = get(policy, "acceptedOffer.premium.loadings[0].id", null)
	return loadings ? true : false
}

// Return formatted loadings array of a policy
const getLoadings = policy => {
	const loadings = get(policy, "acceptedOffer.premium.loadings")
	if (loadings)
		return loadings.map((item, index) => {
			return `${item.codeName.code} (${item.ratio *
				100}%) (${item.currencyCode + item.flat})`
		})
}

// Return loading code based on index param
const getLoadingCode = (policy, index) => {
	const loadings = get(policy, "acceptedOffer.premium.loadings", [])
	if (loadings) {
		let loadingsArray = loadings.map(item => {
			return item.codeName.code
		})
		return loadingsArray[index]
	} else return null
}

// Return accumulated ratio in loadings array
const getLoadingsRatioTotal = policy => {
	const loadings = get(policy, "acceptedOffer.premium.loadings")
	if (loadings)
		return loadings
			.map(item => item.ratio)
			.reduce((a, b) => {
				return a + b
			}, 0)
	else return ""
}

// Go through address and check if placeOfResidence === hong_kong
const isHongKongResident = addresses => {
	const placeOfResidence = addresses
		? addresses
				.filter(item => item.type === "placeOfResidence")
				.map(item => {
					const fields = JSON.parse(item.fields)
					return fields.address
				})
				.toString()
		: null

	if (placeOfResidence === "hong_kong" || placeOfResidence === "Hong Kong") {
		return true
	} else return false
}

// Return optional benefits of policy
const getOptionalBenefits = policy => {
	const benefits = get(policy, "acceptedOffer.product.benefits")
	return benefits.name
}

// Return formatted Contacts Array
const getContacts = contacts => {
	if (contacts)
		return contacts.map(item => {
			return `${item.type}: ${item.value}`
		})
}

// Return formatted Identities Array
const getIdentities = identities => {
	if (identities)
		return identities.map(item => {
			return `${item.type}: ${item.value}`
		})
}

// Return formatted addresses array
const getAddresses = addresses => {
	if (addresses)
		return addresses.map(item => {
			const fields = JSON.parse(item.fields)
			return `${Object.keys(fields)}: ${Object.values(fields)}`
		})
}

// Return formatted facts array
const getFacts = facts => {
	if (facts)
		return facts.map(item => {
			return `${item.type
				.replace(/([A-Z])/g, " $1")
				.toLowerCase()}: ${parseFactValue(item.value)}`
		})
}

// Return formatted transactions array
const getTransactions = policy => {
	const transactions = get(policy, "transactions")
	if (transactions)
		return transactions.map((item, index) => {
			return `${dayjs(item.dateTime).format("DD/MM/YYYY HH:mm:ss:Z")} (${
				item.currencyCode
			}${item.amount}) (${item.method}) (${item.status})`
		})
}

// Return formatted price breakdown of pricing
// (includes gross premium, loadings,  appliedDiscounts, appliedTaxes, net premium)
const getPriceBreakdown = pricing => {
	const grossPremium = get(pricing, "formattedOriginalPrice")
		? `Gross Premium: ${get(pricing, "formattedOriginalPrice")},`
		: ""

	const loadings = get(pricing, "loadings", null)
	const discounts = get(pricing, "appliedDiscounts", [])
	const taxes = get(pricing, "appliedTaxes", [])
	// combine taxes and discounts
	const discountAndTaxes = discounts && taxes ? discounts.concat(taxes) : null
	const netPremium = get(pricing, "formattedPrice")
		? `Net Premium: ${get(pricing, "formattedPrice")}`
		: ""

	const formattedLoadings = loadings
		? loadings.map(item => {
				return `${item?.codeName?.code} (${item.currencyCode +
					item.flat}) (${item.ratio * 100}% of ${item.originalPrice})`
		  })
		: null

	const displayedLoadings = formattedLoadings
		? `Loadings: ${formattedLoadings},`
		: ""

	const formattedDiscountAndTaxes = discountAndTaxes
		? discountAndTaxes
				.sort((a, b) => a.order - b.order)
				.map(item => {
					return `${item.code} (${item.formattedFlat}) (${item.ratio *
						100}% of ${item.originalPrice})`
				})
		: null

	const displayedDiscountAndTaxes = formattedDiscountAndTaxes
		? `Taxes and Discounts: ${formattedDiscountAndTaxes},`
		: ""

	return `${grossPremium} ${displayedLoadings} ${displayedDiscountAndTaxes} ${netPremium}`
}

export {
	getPremiumBeforeDiscounts,
	isHongKongResident,
	hasLoadings,
	hasExclusions,
	getOriginalPrice,
	getDiscounts,
	getExclusions,
	getOptionalBenefits,
	getRejectionCodesArray,
	getRejectionRemarks,
	getLoadingCode,
	getLoadingsRatioTotal,
	getExclusionCode,
	getRejectionCode,
	getLevyRatio,
	getLevyFlat,
	getContacts,
	getIdentities,
	getLoadings,
	getAddresses,
	getFacts,
	getTransactions,
	getCancellationReason,
	getPriceBreakdown
}
